import React from "react";
import PluginPlaceholder from "../../_editor/components/canvas/plugin_placeholder/PluginPlaceholder";
import i18n from "i18next";
import { contentT } from "../../common/content_language";
import "./_freeResponse.scss";
import { setRgbaAlpha, getButtonText } from "../../common/common_tools";
import AnswerButton from "./visor/SubmitAnswerButton";
import { answerLength } from "./answerLength";
import { getInputStyles } from "./inputStyles";
/* eslint-disable react/prop-types */

export function FreeResponse(base) {
  return {
    getConfig: function () {
      return {
        name: "FreeResponse",
        displayName: IdealStudio?.i18n?.t
          ? IdealStudio.i18n.t("FreeResponse.PluginName")
          : i18n.t("FreeResponse.PluginName"),
        category: "evaluation",
        icon: "message",
        initialWidth: "60%",
        // initialHeight: '60%',
        flavor: "react",
        isComplex: true,
        defaultCorrectAnswer: "",
      };
    },
    getToolbar: function (state) {
      return {
        main: {
          __name: "Main",
          accordions: {
            __score: {
              __name: i18n.t("configuration"),
              icon: "build",
              buttons: {
                correct: {
                  __name: i18n.t("FreeResponse.Correct"),
                  type: "checkbox",
                  checked: state.correct,
                },
                showFeedback: {
                  __name: i18n.t("FreeResponse.ShowFeedback"),
                  type: "checkbox",
                  checked: state.showFeedback,
                },
                buttons: {
                  __name: i18n.t("MultipleChoice.WhichButtons"),
                  type: "radio",
                  value: state.buttons,
                  options: ["tryAgain", "seeAnswer"],
                  labels: [i18n.t("MultipleChoice.tryAgain"), i18n.t("MultipleChoice.seeAnswer")],
                },
                checkAnswerText: {
                  __name: i18n.t("FreeResponse.CheckAnswerText") || "Check Answer Button Text",
                  type: "text",
                  value: getButtonText(state.checkAnswerText, "MultipleChoice.sendAnswer"),
                },
                tryAgainText: {
                  __name: i18n.t("FreeResponse.TryAgainText") || "Try Again Button Text",
                  type: "text",
                  value: getButtonText(state.tryAgainText, "MultipleChoice.tryAgain"),
                  hide: state.buttons !== "tryAgain",
                },
                seeAnswerText: {
                  __name: i18n.t("FreeResponse.SeeAnswerText") || "See Answer Button Text",
                  type: "text",
                  value: getButtonText(state.seeAnswerText, "MultipleChoice.seeAnswer"),
                  hide: state.buttons !== "seeAnswer",
                },
                minLength: {
                  __name: i18n.t("FreeResponse.MinLength") || "Minimum Length",
                  type: "number",
                  value: state.minLength || 0,
                  min: 0,
                  // Cross-bounded: min can never exceed the current max (and vice versa).
                  max: state.maxLength || 1000,
                },
                maxLength: {
                  __name: i18n.t("FreeResponse.MaxLength") || "Maximum Length",
                  type: "number",
                  value: state.maxLength || 1000,
                  min: Math.max(1, state.minLength || 0),
                  max: 1000,
                },
                quizColor: {
                  __name:
                    (IdealStudio?.i18n?.t && IdealStudio.i18n.t("FreeResponse.FeedbackColor")) ||
                    i18n.t("FreeResponse.FeedbackColor"),
                  type: "color",
                  value: state.quizColor || "rgba(250, 250, 250, 100)",
                },
                characters: {
                  __name: i18n.t("FreeResponse.Characters"),
                  type: "checkbox",
                  checked: state.characters,
                  hide: state.type !== "text",
                },
              },
            },
            __buttonStyle: {
              __name:
                (IdealStudio?.i18n?.t && IdealStudio.i18n.t("FreeResponse.ButtonStyle")) ||
                "Button Style",
              icon: "palette",
              buttons: {
                buttonBackgroundColor: {
                  __name:
                    (IdealStudio?.i18n?.t && IdealStudio.i18n.t("FreeResponse.ButtonBackground")) ||
                    "Button Background Color",
                  type: "color",
                  value: state.buttonBackgroundColor || "#FAFAFA",
                },
                buttonTextColor: {
                  __name:
                    (IdealStudio?.i18n?.t && IdealStudio.i18n.t("FreeResponse.ButtonTextColor")) ||
                    "Button Text Color",
                  type: "color",
                  value: state.buttonTextColor || "#000000",
                },
                buttonBorderColor: {
                  __name:
                    (IdealStudio?.i18n?.t &&
                      IdealStudio.i18n.t("FreeResponse.ButtonBorderColor")) ||
                    "Button Border Color",
                  type: "color",
                  value: state.buttonBorderColor || "#E5E5E5",
                },
                buttonHoverBackgroundColor: {
                  __name:
                    (IdealStudio?.i18n?.t &&
                      IdealStudio.i18n.t("FreeResponse.ButtonHoverBackground")) ||
                    "Button Hover Background Color",
                  type: "color",
                  value: state.buttonHoverBackgroundColor || "#F0F0F0",
                },
                advancedSettings: {
                  __name:
                    (IdealStudio?.i18n?.t && IdealStudio.i18n.t("AdvancedSettings")) ||
                    "Advanced Settings",
                  type: "checkbox",
                  checked: state.advancedSettings || false,
                },
                buttonBorderWidth: {
                  __name:
                    (IdealStudio?.i18n?.t &&
                      IdealStudio.i18n.t("FreeResponse.ButtonBorderWidth")) ||
                    "Button Border Width",
                  type: "range",
                  value: state.buttonBorderWidth ?? 1,
                  min: 0,
                  max: 5,
                  step: 1,
                  hide: !state.advancedSettings,
                },
                buttonBorderRadius: {
                  __name:
                    (IdealStudio?.i18n?.t &&
                      IdealStudio.i18n.t("FreeResponse.ButtonBorderRadius")) ||
                    "Button Border Radius",
                  type: "range",
                  value: state.buttonBorderRadius ?? 8,
                  min: 0,
                  max: 25,
                  step: 1,
                  hide: !state.advancedSettings,
                },
                buttonPadding: {
                  __name:
                    (IdealStudio?.i18n?.t && IdealStudio.i18n.t("FreeResponse.ButtonPadding")) ||
                    "Button Padding",
                  type: "range",
                  value: state.buttonPadding ?? 8,
                  min: 4,
                  max: 20,
                  step: 1,
                  hide: !state.advancedSettings,
                },
                buttonFontSize: {
                  __name:
                    (IdealStudio?.i18n?.t && IdealStudio.i18n.t("FreeResponse.ButtonFontSize")) ||
                    "Button Font Size",
                  type: "range",
                  value: state.buttonFontSize || 14,
                  min: 10,
                  max: 24,
                  step: 1,
                  hide: !state.advancedSettings,
                },
                buttonFontWeight: {
                  __name:
                    (IdealStudio?.i18n?.t && IdealStudio.i18n.t("FreeResponse.ButtonFontWeight")) ||
                    "Button Font Weight",
                  type: "select",
                  value: state.buttonFontWeight || "normal",
                  options: [
                    "normal",
                    "bold",
                    "100",
                    "200",
                    "300",
                    "400",
                    "500",
                    "600",
                    "700",
                    "800",
                    "900",
                  ],
                  labels: [
                    i18n.t("styles.font_weight.normal") || "normal",
                    i18n.t("styles.font_weight.bold") || "bold",
                    i18n.t("styles.font_weight.100") || "100",
                    i18n.t("styles.font_weight.200") || "200",
                    i18n.t("styles.font_weight.300") || "300",
                    i18n.t("styles.font_weight.400") || "400",
                    i18n.t("styles.font_weight.500") || "500",
                    i18n.t("styles.font_weight.600") || "600",
                    i18n.t("styles.font_weight.700") || "700",
                    i18n.t("styles.font_weight.800") || "800",
                    i18n.t("styles.font_weight.900") || "900",
                  ],
                  hide: !state.advancedSettings,
                },
              },
            },
            __inputStyle: {
              __name:
                (IdealStudio?.i18n?.t && IdealStudio.i18n.t("FreeResponse.InputAppearance")) ||
                i18n.t("FreeResponse.InputAppearance"),
              icon: "edit",
              buttons: {
                inputFontFamily: {
                  __name:
                    (IdealStudio?.i18n?.t && IdealStudio.i18n.t("FreeResponse.InputFont")) ||
                    i18n.t("FreeResponse.InputFont"),
                  type: "select",
                  value: state.inputFontFamily || "Arial",
                  options: [
                    "Arial",
                    "Times New Roman",
                    "Helvetica",
                    "Georgia",
                    "Verdana",
                    "Comic Sans MS",
                    "Trebuchet MS",
                    "Courier New",
                    "Impact",
                    "Lucida Console",
                  ],
                },
                inputBackgroundColor: {
                  __name:
                    (IdealStudio?.i18n?.t && IdealStudio.i18n.t("FreeResponse.InputBackground")) ||
                    i18n.t("FreeResponse.InputBackground"),
                  type: "color",
                  value: state.inputBackgroundColor || "#ffffff",
                },
                inputTextColor: {
                  __name:
                    (IdealStudio?.i18n?.t && IdealStudio.i18n.t("FreeResponse.InputTextColor")) ||
                    i18n.t("FreeResponse.InputTextColor"),
                  type: "color",
                  value: state.inputTextColor || "#000000",
                },
                inputHeight: {
                  __name:
                    (IdealStudio?.i18n?.t && IdealStudio.i18n.t("FreeResponse.InputHeight")) ||
                    i18n.t("FreeResponse.InputHeight"),
                  type: "range",
                  value: state.inputHeight || 100,
                  min: 50,
                  max: 300,
                  step: 10,
                },
                inputFontSize: {
                  __name:
                    (IdealStudio?.i18n?.t && IdealStudio.i18n.t("FreeResponse.InputFontSize")) ||
                    i18n.t("FreeResponse.InputFontSize"),
                  type: "range",
                  value: state.inputFontSize || 14,
                  min: 8,
                  max: 32,
                  step: 1,
                },
                inputBorderColor: {
                  __name:
                    (IdealStudio?.i18n?.t && IdealStudio.i18n.t("FreeResponse.InputBorderColor")) ||
                    i18n.t("FreeResponse.InputBorderColor"),
                  type: "color",
                  value: state.inputBorderColor || "#ced4da",
                },
                inputBorderWidth: {
                  __name:
                    (IdealStudio?.i18n?.t && IdealStudio.i18n.t("FreeResponse.InputBorderWidth")) ||
                    i18n.t("FreeResponse.InputBorderWidth"),
                  type: "range",
                  value: state.inputBorderWidth || 1,
                  min: 0,
                  max: 6,
                  step: 1,
                },
                inputBorderRadius: {
                  __name:
                    (IdealStudio?.i18n?.t &&
                      IdealStudio.i18n.t("FreeResponse.InputBorderRadius")) ||
                    i18n.t("FreeResponse.InputBorderRadius"),
                  type: "range",
                  value: state.inputBorderRadius ?? 4,
                  min: 0,
                  max: 20,
                  step: 1,
                },
              },
            },
            style: {
              __name:
                (IdealStudio?.i18n?.t && IdealStudio.i18n.t("HotspotImages.box_style")) ||
                i18n.t("HotspotImages.box_style"),
              icon: "palette",
              buttons: {
                padding: {
                  __name:
                    (IdealStudio?.i18n?.t && IdealStudio.i18n.t("HotspotImages.padding")) ||
                    i18n.t("HotspotImages.padding"),
                  type: "number",
                  value: 10,
                  min: 0,
                  max: 100,
                },
                backgroundColor: {
                  __name:
                    (IdealStudio?.i18n?.t &&
                      IdealStudio.i18n.t("HotspotImages.background_color")) ||
                    i18n.t("HotspotImages.background_color"),
                  type: "color",
                  value: "#ffffff",
                },
                borderWidth: {
                  __name:
                    (IdealStudio?.i18n?.t && IdealStudio.i18n.t("HotspotImages.border_size")) ||
                    i18n.t("HotspotImages.border_size"),
                  type: "number",
                  value: 1,
                  min: 0,
                  max: 10,
                },
                borderStyle: {
                  __name:
                    (IdealStudio?.i18n?.t && IdealStudio.i18n.t("HotspotImages.border_style")) ||
                    i18n.t("HotspotImages.border_style"),
                  type: "select",
                  value: "solid",
                  options: [
                    "none",
                    "hidden",
                    "dotted",
                    "dashed",
                    "solid",
                    "double",
                    "groove",
                    "ridge",
                    "inset",
                    "outset",
                    "initial",
                    "inherit",
                  ],
                  labels: [
                    i18n.t("styles.border_style_options.none") || "none",
                    i18n.t("styles.border_style_options.hidden") || "hidden",
                    i18n.t("styles.border_style_options.dotted") || "dotted",
                    i18n.t("styles.border_style_options.dashed") || "dashed",
                    i18n.t("styles.border_style_options.solid") || "solid",
                    i18n.t("styles.border_style_options.double") || "double",
                    i18n.t("styles.border_style_options.groove") || "groove",
                    i18n.t("styles.border_style_options.ridge") || "ridge",
                    i18n.t("styles.border_style_options.inset") || "inset",
                    i18n.t("styles.border_style_options.outset") || "outset",
                    i18n.t("styles.border_style_options.initial") || "initial",
                    i18n.t("styles.border_style_options.inherit") || "inherit",
                  ],
                },
                borderColor: {
                  __name:
                    (IdealStudio?.i18n?.t && IdealStudio.i18n.t("HotspotImages.border_color")) ||
                    i18n.t("HotspotImages.border_color"),
                  type: "color",
                  value: "#dbdbdb",
                },
                borderRadius: {
                  __name:
                    (IdealStudio?.i18n?.t && IdealStudio.i18n.t("HotspotImages.radius")) ||
                    i18n.t("HotspotImages.radius"),
                  type: "number",
                  value: 0,
                  min: 0,
                  max: 50,
                },
                opacity: {
                  __name:
                    (IdealStudio?.i18n?.t && IdealStudio.i18n.t("HotspotImages.opacity")) ||
                    i18n.t("HotspotImages.opacity"),
                  type: "range",
                  value: 1,
                  min: 0,
                  max: 1,
                  step: 0.01,
                },
              },
            },
          },
        },
      };
    },
    getInitialState: function () {
      return {
        showFeedback: false,
        characters: true,
        correct: true,
        quizColor: "rgba(250, 250, 250, 100)",
        buttons: "tryAgain",
        checkAnswerText: null,
        tryAgainText: null,
        seeAnswerText: null,
        buttonBackgroundColor: "#FAFAFA",
        buttonTextColor: "#000000",
        buttonBorderColor: "#E5E5E5",
        buttonBorderWidth: 1,
        buttonBorderRadius: 8,
        buttonPadding: 8,
        buttonFontSize: 14,
        buttonFontWeight: "normal",
        buttonHoverBackgroundColor: "#F0F0F0",
        advancedSettings: false,
        inputBackgroundColor: "#ffffff",
        inputHeight: 100,
        inputFontSize: 14,
        inputFontFamily: "Arial",
        inputTextColor: "#000000",
        inputBorderColor: "#ced4da",
        inputBorderWidth: 1,
        inputBorderRadius: 4,
        minLength: 0,
        maxLength: 200,
        excludeSpaces: false,
      };
    },
    getRenderTemplate: function (state, props) {
      let maxLength = state.maxLength || 1000;
      let minLength = state.minLength || 0;
      let correctAnswerLength = answerLength(props.exercises.correctAnswer, state);

      let clickHandler = (e) => {
        // Native maxLength counts whitespace, so with excludeSpaces the limit is
        // enforced here instead (spaces stay typeable).
        if (state.excludeSpaces && answerLength(e.target.value, state) > maxLength) {
          return;
        }
        props.setCorrectAnswer(e.target.value);
      };

      let quizColor = state.quizColor || "rgba(250, 250, 250, 100)";

      // Create dynamic styles for textarea input
      let inputStyles = getInputStyles(state);

      return (
        <div
          className={"exercisePlugin freeResponsePlugin"}
          style={{ "--q-btn-hover-bg": state.buttonHoverBackgroundColor || "#F0F0F0" }}
        >
          {" "}
          {/* <h1>Free Response</h1>*/}
          <div className={"row"} key={0}>
            <div className={"col-xs-12"}>
              <PluginPlaceholder
                {...props}
                key="1"
                pluginContainerName={i18n.t("FreeResponse.Question")}
                pluginDefaultContent={[
                  {
                    plugin: "BasicText",
                    initialState: { __i18nKey: "FreeResponse.Statement" },
                  },
                ]}
                pluginContainer={"Question"}
              />
              <textarea
                disabled={!state.correct}
                className="form-control textAreaQuiz dynamic-height"
                style={inputStyles}
                placeholder={i18n.t("FreeResponse.PlaceholderEditor")}
                value={props.exercises.correctAnswer}
                onChange={clickHandler}
                maxLength={state.excludeSpaces ? undefined : maxLength}
              />
              {state.correct && correctAnswerLength > maxLength ? (
                <div className={"tooManyCharacters"}>{contentT("FreeResponse.TooMany")}</div>
              ) : null}
              {state.correct &&
              correctAnswerLength > 0 &&
              correctAnswerLength < minLength &&
              minLength > 0 ? (
                <div className={"tooFewCharacters"}>
                  {contentT("FreeResponse.TooFew") ||
                    `Minimum ${state.minLength || 0} characters required`}
                </div>
              ) : null}
            </div>
          </div>
          <div
            className={"row feedbackRow"}
            key={-2}
            style={{ display: state.showFeedback ? "block" : "none" }}
          >
            <div
              className={"col-xs-12 feedback"}
              style={{
                color: /* quizColor */ "black",
                borderColor: "#318533",
                backgroundColor: setRgbaAlpha("rgba(39,208,124,0.20)", 0.15),
              }}
            >
              <PluginPlaceholder
                {...props}
                key="-2"
                pluginContainerName={i18n.t("MultipleChoice.Feedback")}
                pluginContainer={"TrueFeedback"}
                pluginDefaultContent={[
                  {
                    plugin: "BasicText",
                    initialState: { __i18nKey: "MultipleChoice.TrueFeedbackMsg" },
                  },
                ]}
              />
            </div>
          </div>
          <div
            className={"row feedbackRow"}
            key={-3}
            style={{ display: state.showFeedback ? "block" : "none" }}
          >
            <div
              className={"col-xs-12 feedback"}
              style={{
                color: /* quizColor */ "black",
                borderColor: "#a31111",
                backgroundColor: setRgbaAlpha("rgba(213,26,26,0.20)", 0.15),
              }}
            >
              <PluginPlaceholder
                {...props}
                key="-3"
                pluginContainerName={i18n.t("MultipleChoice.Feedback")}
                pluginContainer={"FalseFeedback"}
                pluginDefaultContent={[
                  {
                    plugin: "BasicText",
                    initialState: { __i18nKey: "MultipleChoice.FalseFeedbackMsg" },
                  },
                ]}
              />
            </div>
          </div>
          <div className={"row"} key={7}>
            <AnswerButton
              onClick={(e) => e.preventDefault()}
              disabled={false}
              hidden={state.buttons !== "tryAgain"}
              text={getButtonText(state.tryAgainText, "FreeResponse.tryAgain")}
              fontSize={state.buttonFontSize}
              fontWeight={state.buttonFontWeight}
              settings={state}
            />
            <AnswerButton
              onClick={(e) => e.preventDefault()}
              disabled={false}
              hidden={false}
              text={getButtonText(state.checkAnswerText, "FreeResponse.checkAnswer")}
              fontSize={state.buttonFontSize}
              fontWeight={state.buttonFontWeight}
              settings={state}
            />
            <AnswerButton
              onClick={(e) => e.preventDefault()}
              disabled={false}
              hidden={state.buttons !== "seeAnswer"}
              text={getButtonText(state.seeAnswerText, "FreeResponse.seeAnswer")}
              fontSize={state.buttonFontSize}
              fontWeight={state.buttonFontWeight}
              settings={state}
            />
          </div>
          <div style={{ textAlign: "right", marginTop: "6px" }}>
            <label
              style={{
                fontSize: "0.8em",
                color: "#6b7280",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              {i18n.t("FreeResponse.MaxInput") || "Max characters"}
              <input
                type="number"
                min={1}
                max={5000}
                value={state.maxLength || 200}
                onChange={(e) => props.update("maxLength", parseInt(e.target.value, 10) || 200)}
                style={{
                  width: "72px",
                  padding: "2px 6px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                }}
              />
            </label>
          </div>
        </div>
      );
    },
  };
}
/* eslint-enable react/prop-types */
